/**
 * Audio device helpers shared by the transports, the store and the widget.
 *
 * Device selection is per agent, per browser, and works in every softphone state - offline included,
 * when there is no transport at all - so listing devices is done here, straight off
 * `navigator.mediaDevices`, rather than through a transport that may not exist yet. Only APPLYING a
 * selection needs a transport, because only a transport has a client and a call to apply it to.
 */

/**
 * What every microphone capture asks for. Sent in full every time, because `setAudioSettings`
 * replaces the session's constraint object wholesale: a selection that sent `micId` alone would
 * silently drop echo cancellation for every call after it. See `_applyMicrophoneSettings` in
 * TelnyxSoftphoneTransport for why the floor exists at all.
 */
export const MICROPHONE_CONSTRAINT_FLOOR = Object.freeze({
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true
})

/**
 * The one definition of the settings object handed to the SDK's `setAudioSettings`, for go-live and
 * for a selection alike. `null` is "System default": no `micId` at all, so the browser follows the
 * operating system rather than being pinned to whatever the default happened to be when chosen.
 */
export function buildMicrophoneSettings(deviceId = null) {
  return deviceId
    ? { ...MICROPHONE_CONSTRAINT_FLOOR, micId: deviceId }
    : { ...MICROPHONE_CONSTRAINT_FLOOR }
}

/**
 * Why a selection did not take. The widget words its error from this, so each one has to mean
 * something the agent can act on.
 *
 * - `unsupported` the platform cannot route audio to a chosen output (no `setSinkId`)
 * - `notFound`    the device is not there any more - unplugged, or its id rotated
 * - `failed`      the device is there and the browser would not use it (in use elsewhere, refused)
 * - `otherTab`    the softphone is live in another tab, which is the only one that can apply it
 */
export const AUDIO_DEVICE_FAILURES = Object.freeze({
  UNSUPPORTED: 'unsupported',
  NOT_FOUND: 'notFound',
  FAILED: 'failed',
  OTHER_TAB: 'otherTab'
})

/**
 * What every `setAudio*Device` resolves to. A result object rather than a boolean or a throw,
 * because "it worked" comes in three shapes the agent is told apart:
 *
 * - applied now, to the client or the call in progress
 * - remembered for the next Go Live (`isDeferred`), because nothing is connected to apply it to
 * - applied, but the replacement microphone track would not take the echo-cancellation floor
 *   (`isConstraintFloorMissing`) - the device change stands, the call is not dropped, and the
 *   agent is warned that the caller may hear an echo
 */
export function appliedDeviceResult({ isDeferred = false, isConstraintFloorMissing = false } = {}) {
  return { ok: true, isDeferred, isConstraintFloorMissing }
}

export function failedDeviceResult(reason = AUDIO_DEVICE_FAILURES.FAILED, error = null) {
  return { ok: false, reason, error }
}

/**
 * Chrome lists two pseudo-devices ahead of the real ones: `default` and, on Windows,
 * `communications`. Both are aliases for a real entry further down the list, and the widget's own
 * "System default" option already means the first, so offering them again would show the agent the
 * same headset three times.
 */
const PSEUDO_DEVICE_IDS = Object.freeze(['default', 'communications'])

function _hasMediaDevices() {
  return typeof navigator !== 'undefined'
    && Boolean(navigator.mediaDevices)
    && typeof navigator.mediaDevices.enumerateDevices === 'function'
}

export async function enumerateAudioDevices() {
  if (!_hasMediaDevices()) {
    return []
  }

  try {
    return await navigator.mediaDevices.enumerateDevices()
  } catch (error) {
    console.warn('Could not list the audio devices', error)

    return []
  }
}

function _toEntries(devices, kind) {
  return devices
    .filter(device => device.kind === kind && !PSEUDO_DEVICE_IDS.includes(device.deviceId))
    .map(device => ({ deviceId: device.deviceId, label: device.label || '', groupId: device.groupId || '' }))
}

/**
 * Both lists from one enumeration, so they always describe the same moment. `hasLabels` is false
 * when the browser handed back entries with blank labels - which is what it does until microphone
 * permission has been granted - and the panel shows a permission line instead of a list of blanks.
 */
export async function listAudioDevices() {
  const devices = await enumerateAudioDevices()
  const inputs = _toEntries(devices, 'audioinput')
  const outputs = _toEntries(devices, 'audiooutput')
  const all = [...inputs, ...outputs]

  return {
    inputs,
    outputs,
    hasLabels: all.length > 0 && all.every(device => device.label)
  }
}

/**
 * The device "System default" means right now, for the one path that cannot take "no device": the
 * SDK's mid-call `setAudioInDevice`, which always captures with `{ deviceId: { exact } }`.
 *
 * Chrome names it - the `default` pseudo-device. Firefox and Safari do not, and put the default
 * input first, so the first real input stands in for it there.
 */
export async function resolveDefaultInputDeviceId() {
  const devices = (await enumerateAudioDevices()).filter(device => device.kind === 'audioinput')

  const named = devices.find(device => device.deviceId === 'default')

  return named?.deviceId || devices[0]?.deviceId || null
}

export function isDeviceNotFoundError(error) {
  return error?.name === 'NotFoundError' || error?.name === 'OverconstrainedError'
}

/**
 * Points a media element at an output device. `''` is how `setSinkId` spells the browser default,
 * and it has to be spelled out: the SDK's own helper rejects anything but a non-empty string
 * (`Invalid speaker deviceId`), which would leave a previously pinned sink in place and keep the
 * caller in a headset the agent has just deselected.
 */
export async function routeElementToOutput(element, deviceId) {
  if (!element || typeof element.setSinkId !== 'function') {
    return failedDeviceResult(AUDIO_DEVICE_FAILURES.UNSUPPORTED)
  }

  try {
    await element.setSinkId(deviceId || '')

    return appliedDeviceResult()
  } catch (error) {
    console.warn('Could not route the softphone audio to the chosen speaker', error)

    return failedDeviceResult(
      isDeviceNotFoundError(error) ? AUDIO_DEVICE_FAILURES.NOT_FOUND : AUDIO_DEVICE_FAILURES.FAILED,
      error
    )
  }
}
